<template>
  <!-- Desktop View -->
  <div class="price-summary" v-if="!isMobile">
    <div v-if="locale === 'SG'" class="price-row">
      <span class="price-label">Total Activities</span>
      <span class="price-label">{{ totalActivities }} Activites</span>
    </div>
    <!-- Price Breakdown -->
    <div class="price-breakdown" v-if="locale === 'US'">
      <!-- Custom makercart subtotal -->
      <div v-if="hasBundledActivities" class="price-row">
        <span class="price-label">Custom makercart subtotal</span>
        <span class="price-value">${{ formatPrice(total.percartTotal) }}</span>
      </div>

      <!-- Signature subtotal -->
      <div v-if="hasSignatureMakercarts" class="price-row">
        <span class="price-label">Signature subtotal</span>
        <span class="price-value"
          >${{ formatPrice(total.signaturePrice) }}</span
        >
      </div>

      <!-- Makercrate subtotal -->
      <div v-if="hasMakercrates" class="price-row">
        <span class="price-label">Makercrate subtotal</span>
        <span class="price-value"
          >${{ formatPrice(total.makercratePrice) }}</span
        >
      </div>

      <!-- Single activity subtotal -->
      <div v-if="hasSingleActivities" class="price-row">
        <span class="price-label">Single activity subtotal</span>
        <span class="price-value"
          >${{ formatPrice(total.singleSubtotal) }}</span
        >
      </div>

      <!-- Posters subtotal -->
      <div v-if="total.posterPrice > 0" class="price-row">
        <span class="price-label">Posters subtotal</span>
        <span class="price-value">${{ formatPrice(total.posterPrice) }}</span>
      </div>

      <!-- Divider before total -->
      <div class="price-divider"></div>

      <!-- Total -->
      <div class="price-row total">
        <p class="price-label">
          <span class="price-label">{{
            isMakercrateOnlyCart ? "Total" : "Grand Total"
          }}</span>
          <span class="price-label-currency"> ({{ localeCurrency }})</span>
        </p>
        <span class="price-value bold"
          >${{ formatPrice(total.grandTotal) }}</span
        >
      </div>
    </div>

    <!-- Divider before form -->
    <div
      v-if="(showQuoteForm && !quoteGenerated) || locale === 'SG'"
      class="price-divider"
    ></div>

    <!-- Quote Form Section -->
    <transition name="form-fade">
      <div
        v-if="(showQuoteForm && !quoteGenerated) || locale === 'SG'"
        class="quote-form-section"
      >
        <div class="form-badge">
          <span class="badge-text">Who should we address the quote to?</span>
        </div>

        <div class="form-field">
          <label class="form-label">
            Organization name<span class="required">*</span>
          </label>
          <input
            type="text"
            :value="quoteFormData.organization_name"
            @input="updateField('organization_name', $event.target.value)"
            :disabled="quoteGenerated || isRequestingQuote"
            placeholder="Fill the organization name"
            class="form-input"
          />
        </div>

        <div class="form-field">
          <label class="form-label">
            Name<span class="required">*</span>
          </label>
          <input
            type="text"
            :value="quoteFormData.attention_to"
            @input="updateField('attention_to', $event.target.value)"
            placeholder="Fill the name"
            :disabled="quoteGenerated || isRequestingQuote"
            class="form-input"
          />
        </div>

        <div class="form-field">
          <label class="form-label">
            City<span class="required">*</span>
          </label>
          <input
            type="text"
            :value="quoteFormData.city"
            @input="updateField('city', $event.target.value)"
            :disabled="quoteGenerated || isRequestingQuote"
            placeholder="Fill the city"
            class="form-input"
          />
        </div>

        <div class="form-field">
          <label class="form-label">
            State<span class="required">*</span>
          </label>
          <input
            type="text"
            :value="quoteFormData.state"
            @input="updateField('state', $event.target.value)"
            :disabled="quoteGenerated || isRequestingQuote"
            placeholder="Fill in the state"
            class="form-input"
          />
        </div>

        <div class="form-field">
          <label class="form-label">
            Address<span class="required">*</span>
          </label>
          <textarea
            :value="quoteFormData.address"
            @input="updateField('address', $event.target.value)"
            :disabled="quoteGenerated || isRequestingQuote"
            placeholder="Fill the address"
            class="form-textarea"
          ></textarea>
        </div>
      </div>
    </transition>

    <template v-if="locale === 'US'">
      <ActionButton
        v-if="!quoteGenerated && isMakercrateOnlyCart && !showQuoteForm"
        variant="primary"
        full-width
        :disabled="total.grandTotal <= 0"
        @click="$emit('checkout')"
      >
        <span>Checkout</span>
        <template #icon-right>
          <ShoppingCartIcon
            width="24"
            height="24"
            :color="total.grandTotal <= 0 ? '#6C737F' : '#ffff'"
          />
        </template>
      </ActionButton>

      <ActionButton
        v-if="!quoteGenerated"
        :variant="quoteButtonVariant"
        :disabled="
          total.grandTotal <= 0 ||
          (showQuoteForm && (!isFormValid || isRequestingQuote))
        "
        :loading="isRequestingQuote"
        @click="$emit('request-quote')"
      >
        <span>{{
          isMakercrateOnlyCart || showQuoteForm ? "Generate a Quote" : "Next"
        }}</span>
        <template #icon-right>
          <ChevronDownIcon
            v-if="!showQuoteForm && !isMakercrateOnlyCart"
            width="16"
            height="16"
            :color="
              total.grandTotal <= 0 ||
              (showQuoteForm && (!isFormValid || isRequestingQuote))
                ? '#6C737F'
                : quoteButtonVariant === 'secondary'
                  ? '#003d6a'
                  : '#ffff'
            "
          />
          <DocumentTextIcon
            v-else
            :color="
              total.grandTotal <= 0 ||
              (showQuoteForm && (!isFormValid || isRequestingQuote))
                ? '#6C737F'
                : quoteButtonVariant === 'secondary'
                  ? '#003d6a'
                  : '#ffff'
            "
            class="quote-icon"
          />
        </template>
      </ActionButton>

      <ActionButton
        v-if="isCrateQuoteFormOpen"
        variant="text"
        full-width
        :disabled="isRequestingQuote"
        @click="handleCancelQuote"
      >
        <span>Cancel</span>
        <template #icon-right>
          <span class="cancel-x-icon">
            <img
              src="/images/icon/cancel-x.svg"
              alt=""
              width="24"
              height="24"
            />
          </span>
        </template>
      </ActionButton>

      <!-- Quote Generated Buttons -->
      <div v-if="quoteGenerated" class="quote-buttons">
        <ActionButton
          variant="primary"
          @click="$emit('download-quote')"
          class="download-quote-btn"
          :loading="isDownloadingQuote"
          loadingText="Downloading Quote..."
          :disabled="isDownloadingQuote"
        >
          <span>Download Quote</span>
        </ActionButton>
        <ActionButton
          variant="secondary"
          @click="$emit('resend-quote')"
          class="resend-quote-btn"
          :loading="isSendingQuote"
          :disabled="isSendingQuote"
        >
          <span>{{
            isSendingQuote ? "Sending..." : "Resend Quote via Email"
          }}</span>
        </ActionButton>
      </div>
    </template>
    <template v-else>
      <template v-if="!quoteGenerated">
        <div v-if="!hasFullActivityCart" class="tooltip-wrapper">
          <CustomTooltip placement="bottom">
            <template #content>
              <p style="text-align: center">
                A minimum of 8 activities in one Makercart are needed to request
                a quote
              </p>
            </template>
            <template #trigger>
              <ActionButton
                :disabled="true"
                :loading="isRequestingQuote"
                @click="$emit('request-quote')"
                style="width: 100%"
              >
                <span>Request a Quote</span>
                <template #icon-right>
                  <img
                    src="/images/icon/document-icon.svg"
                    alt="Quote Icon"
                    class="quote-icon"
                  />
                </template>
              </ActionButton>
            </template>
          </CustomTooltip>
        </div>
        <ActionButton
          v-else
          :disabled="!isFormValid"
          :loading="isRequestingQuote"
          @click="$emit('request-quote')"
        >
          <span>Request a Quote</span>
          <template #icon-right>
            <img
              src="/images/icon/document-icon.svg"
              alt="Quote Icon"
              class="quote-icon"
            />
          </template>
        </ActionButton>
      </template>
    </template>
  </div>

  <!-- Mobile Collapsed View -->
  <div
    v-else
    class="price-summary-mobile-collapsed"
    @touchstart="handleTouchStart"
    @touchmove="handleTouchMove"
    @touchend="handleTouchEnd"
  >
    <div class="mobile-collapsed-handle"></div>
    <div v-if="locale === 'SG'" class="price-row">
      <span class="price-label">Total Activities</span>
      <span class="price-label">{{ totalActivities }} Activites</span>
    </div>
    <!-- Price Breakdown -->
    <div class="price-breakdown" v-if="locale === 'US'">
      <!-- Custom makercart subtotal -->
      <div v-if="hasBundledActivities" class="price-row">
        <span class="price-label">Custom makercart subtotal</span>
        <span class="price-value">${{ formatPrice(total.percartTotal) }}</span>
      </div>

      <!-- Signature subtotal -->
      <div v-if="hasSignatureMakercarts" class="price-row">
        <span class="price-label">Signature subtotal</span>
        <span class="price-value"
          >${{ formatPrice(total.signaturePrice) }}</span
        >
      </div>

      <!-- Makercrate subtotal -->
      <div v-if="hasMakercrates" class="price-row">
        <span class="price-label">Makercrate subtotal</span>
        <span class="price-value"
          >${{ formatPrice(total.makercratePrice) }}</span
        >
      </div>

      <!-- Single activity subtotal -->
      <div v-if="hasSingleActivities" class="price-row">
        <span class="price-label">Single activity subtotal</span>
        <span class="price-value"
          >${{ formatPrice(total.singleSubtotal) }}</span
        >
      </div>

      <!-- Posters subtotal -->
      <div v-if="total.posterPrice > 0" class="price-row">
        <span class="price-label">Posters subtotal</span>
        <span class="price-value">${{ formatPrice(total.posterPrice) }}</span>
      </div>

      <!-- Divider before total -->
      <div class="price-divider"></div>

      <!-- Total -->
      <div class="price-row total">
        <p class="price-label">
          <span class="price-label">{{
            isMakercrateOnlyCart ? "Total" : "Grand Total"
          }}</span>
          <span class="price-label-currency"> ({{ localeCurrency }})</span>
        </p>
        <span class="price-value bold"
          >${{ formatPrice(total.grandTotal) }}</span
        >
      </div>
    </div>
    <template v-if="locale === 'SG'">
      <template v-if="!quoteGenerated">
        <div v-if="!hasFullActivityCart" class="tooltip-wrapper">
          <CustomTooltip placement="bottom">
            <template #content>
              <p style="text-align: center">
                A minimum of 8 activities in one Makercart are needed to request
                a quote
              </p>
            </template>
            <template #trigger>
              <ActionButton
                :disabled="true"
                :loading="isRequestingQuote"
                @click="$emit('request-quote')"
                style="width: 100%"
              >
                <span>Request a Quote</span>
                <template #icon-right>
                  <img
                    src="/images/icon/document-icon.svg"
                    alt="Quote Icon"
                    class="quote-icon"
                  />
                </template>
              </ActionButton>
            </template>
          </CustomTooltip>
        </div>
        <ActionButton
          v-else
          :disabled="!isFormValid"
          :loading="isRequestingQuote"
          @click="$emit('request-quote')"
        >
          <span>Request a Quote</span>
          <template #icon-right>
            <img
              src="/images/icon/document-icon.svg"
              alt="Quote Icon"
              class="quote-icon"
            />
          </template>
        </ActionButton>
      </template>
    </template>
    <template v-if="locale === 'US'">
      <ActionButton
        v-if="!quoteGenerated && isMakercrateOnlyCart && !showQuoteForm"
        variant="primary"
        full-width
        :disabled="total.grandTotal <= 0"
        @click="$emit('checkout')"
      >
        <span>Checkout</span>
        <template #icon-right>
          <ShoppingCartIcon
            width="24"
            height="24"
            :color="total.grandTotal <= 0 ? '#6C737F' : '#ffff'"
          />
        </template>
      </ActionButton>

      <ActionButton
        v-if="!quoteGenerated"
        :variant="quoteButtonVariant"
        :disabled="
          total.grandTotal <= 0 ||
          (isMobile
            ? isRequestingQuote
            : showQuoteForm && (!isFormValid || isRequestingQuote))
        "
        :loading="isRequestingQuote"
        @click="handleNextClick"
      >
        <span>{{
          isMakercrateOnlyCart || (!isMobile && showQuoteForm)
            ? "Generate a Quote"
            : "Next"
        }}</span>
        <template #icon-right>
          <ChevronDownIcon
            v-if="!isMakercrateOnlyCart && (isMobile || !showQuoteForm)"
            width="16"
            height="16"
            :color="
              total.grandTotal <= 0 ||
              (isMobile
                ? isRequestingQuote
                : showQuoteForm && (!isFormValid || isRequestingQuote))
                ? '#6C737F'
                : quoteButtonVariant === 'secondary'
                  ? '#003d6a'
                  : '#ffff'
            "
          />
          <DocumentTextIcon
            v-else
            :color="
              total.grandTotal <= 0 ||
              (isMobile
                ? isRequestingQuote
                : showQuoteForm && (!isFormValid || isRequestingQuote))
                ? '#6C737F'
                : quoteButtonVariant === 'secondary'
                  ? '#003d6a'
                  : '#ffff'
            "
            class="quote-icon"
          />
        </template>
      </ActionButton>

      <ActionButton
        v-if="isCrateQuoteFormOpen"
        variant="text"
        full-width
        :disabled="isRequestingQuote"
        @click="handleCancelQuote"
      >
        <span>Cancel</span>
        <template #icon-right>
          <span class="cancel-x-icon">
            <img
              src="/images/icon/cancel-x.svg"
              alt=""
              width="24"
              height="24"
            />
          </span>
        </template>
      </ActionButton>
    </template>
  </div>

  <!-- Mobile Bottom Sheet -->
  <OrderSummaryBottomSheet
    v-if="isMobile"
    v-model:visible="showBottomSheet"
    :total="total"
    :locale="locale"
    :localeCurrency="localeCurrency"
    :showQuoteForm="showQuoteForm"
    :quoteGenerated="quoteGenerated"
    :quoteFormData="quoteFormData"
    :isFormValid="isFormValid"
    :isRequestingQuote="isRequestingQuote"
    :isDownloadingQuote="isDownloadingQuote"
    :isSendingQuote="isSendingQuote"
    :hasBundledActivities="hasBundledActivities"
    :hasSignatureMakercarts="hasSignatureMakercarts"
    :hasSingleActivities="hasSingleActivities"
    :hasMakercrates="hasMakercrates"
    :isMakercrateOnlyCart="isMakercrateOnlyCart"
    :totalActivities="totalActivities"
    :hasFullActivityCart="hasFullActivityCart"
    @checkout="$emit('checkout')"
    @request-quote="$emit('request-quote')"
    @cancel-quote="handleCancelQuote"
    @download-quote="$emit('download-quote')"
    @resend-quote="$emit('resend-quote')"
    @update:quoteFormData="(newData) => $emit('update:quoteFormData', newData)"
  />
</template>

<script setup>
/* eslint-disable no-undef */
import ActionButton from "@/components/common/ActionButton.vue";
import CustomTooltip from "@/components/common/CustomTooltip.vue";
import OrderSummaryBottomSheet from "./OrderSummaryBottomSheet.vue";
import {
  ChevronDownIcon,
  DocumentTextIcon,
  ShoppingCartIcon,
} from "@/components/icons";
import { useLocaleStore } from "@/store/locale";
import { formatPrice } from "@/utils/index.js";
import { computed, watch, ref, onMounted, onUnmounted } from "vue";
import { useCart } from "@/composeables/useCart";

const props = defineProps({
  total: {
    type: Object,
    required: true,
  },
  locale: {
    type: String,
    default: "US",
  },
  localeCurrency: {
    type: String,
    default: "USD",
  },
  showQuoteForm: {
    type: Boolean,
    default: false,
  },
  quoteGenerated: {
    type: Boolean,
    default: false,
  },
  quoteFormData: {
    type: Object,
    required: true,
  },
  isFormValid: {
    type: Boolean,
    default: false,
  },
  isRequestingQuote: {
    type: Boolean,
    default: false,
  },
  isDownloadingQuote: {
    type: Boolean,
    default: false,
  },
  isSendingQuote: {
    type: Boolean,
    default: false,
  },
  hasBundledActivities: {
    type: Boolean,
    default: false,
  },
  hasSignatureMakercarts: {
    type: Boolean,
    default: false,
  },
  hasSingleActivities: {
    type: Boolean,
    default: false,
  },
  hasMakercrates: {
    type: Boolean,
    default: false,
  },
  isMakercrateOnlyCart: {
    type: Boolean,
    default: false,
  },
  totalActivities: {
    type: Number,
    default: 0,
  },
});

const localeStore = useLocaleStore();
const locale = computed(() => localeStore.currentLocale);
const { hasFullActivityCart } = useCart();

const emit = defineEmits([
  "checkout",
  "request-quote",
  "cancel-quote",
  "download-quote",
  "resend-quote",
  "update:quoteFormData",
]);

const isCrateQuoteFormOpen = computed(
  () =>
    props.isMakercrateOnlyCart && props.showQuoteForm && !props.quoteGenerated
);

const quoteButtonVariant = computed(() =>
  props.isMakercrateOnlyCart && !props.showQuoteForm ? "secondary" : "primary"
);

const isMobile = ref(false);
const showBottomSheet = ref(false);
const touchStartY = ref(0);
const touchStartTime = ref(0);

const checkMobile = () => {
  isMobile.value = window.innerWidth <= 768;
};

const handleTouchStart = (e) => {
  touchStartY.value = e.touches[0].clientY;
  touchStartTime.value = Date.now();
};

const handleTouchMove = (e) => {
  // Prevent default scrolling if swiping up
  if (touchStartY.value - e.touches[0].clientY > 10) {
    e.preventDefault();
  }
};

const handleTouchEnd = (e) => {
  const touchEndY = e.changedTouches[0].clientY;
  const touchEndTime = Date.now();
  const deltaY = touchStartY.value - touchEndY;
  const deltaTime = touchEndTime - touchStartTime.value;

  // Swipe up gesture: at least 50px upward movement in less than 300ms
  if (deltaY > 50 && deltaTime < 300) {
    showBottomSheet.value = true;
  }
};

const updateField = (field, value) => {
  emit("update:quoteFormData", { ...props.quoteFormData, [field]: value });
};

const handleNextClick = () => {
  if (isMobile.value) {
    showBottomSheet.value = true;
    if (!props.showQuoteForm) {
      emit("request-quote");
    }
  } else {
    if (!props.showQuoteForm) {
      emit("request-quote");
    } else {
      emit("request-quote");
    }
  }
};

const handleCancelQuote = () => {
  showBottomSheet.value = false;
  emit("cancel-quote");
};

watch(
  () => props.quoteGenerated,
  (newVal) => {
    console.log("OrderSummary: quoteGenerated changed to", newVal);
  }
);

onMounted(() => {
  checkMobile();
  window.addEventListener("resize", checkMobile);
});

onUnmounted(() => {
  window.removeEventListener("resize", checkMobile);
});
</script>

<style scoped>
@import "@/assets/css/colors.css";

.price-summary {
  background-color: var(--white);
  border-radius: 12px;
  padding: 12px;
  width: 450px;
  box-shadow:
    0px 1px 2px 0px rgba(0, 0, 0, 0.3),
    0px 2px 6px 2px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* Price Breakdown */
.price-breakdown {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.price-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  white-space: nowrap;
}

.price-label {
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-large);
  font-weight: 400;
  line-height: 28px;
  color: #3b4648;
  margin: 0;
  padding: 0;
}

.price-label-currency {
  font-weight: 400;
  font-size: var(--fs-large);
  line-height: 24px;
  letter-spacing: 0.15%;
  color: var(--primary-purple);
}

.price-value {
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-large);
  font-weight: 400;
  line-height: 28px;
  color: #3b4648;
}

.price-value.bold {
  font-weight: 700;
}

.price-divider {
  width: 100%;
  height: 1px;
  background-color: #e5e7eb;
}

.quote-icon {
  width: 24px;
  height: 24px;
}

.quote-icon--secondary {
  filter: none;
}

.cancel-x-icon {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.cancel-x-icon img {
  width: 24px;
  height: 24px;
  display: block;
}

/* Quote Buttons */
.quote-buttons {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
}

.download-quote-btn,
.resend-quote-btn {
  width: 100%;
  height: 48px;
}

/* Quote Form Section */
.quote-form-section {
  background-color: #eaecff;
  border-radius: 4px;
  padding: 8px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
}

.form-badge {
  background-color: var(--purple-dark-1);
  border-radius: 999px;
  padding: 4px 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  align-self: flex-start;
}

.badge-text {
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-big);
  font-weight: 500;
  line-height: 28px;
  letter-spacing: 0.03px;
  color: var(--white);
  text-align: center;
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
}

.form-label {
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-big);
  font-weight: 400;
  line-height: 28px;
  letter-spacing: 0.03px;
  color: #3b4648;
}

.required {
  color: #e00069;
}

.form-input {
  width: 100%;
  height: 40px;
  padding: 4px 16px;
  border: 1px solid #6c737f;
  border-radius: 8px;
  background-color: var(--white);
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-big);
  font-weight: 400;
  line-height: 28px;
  letter-spacing: 0.03px;
  color: #3b4648;
  outline: none;
  transition: border-color 0.2s ease;
}

.form-input::placeholder {
  color: #6c737f;
  font-weight: 400;
}

.form-input:focus {
  border-color: var(--primary-blue);
}

.form-input:disabled {
  background-color: #f3f4f6;
  cursor: not-allowed;
  color: #9ca3af;
  border-color: #e5e7eb;
}

.form-textarea {
  width: 100%;
  height: 128px;
  padding: 16px;
  border: 1px solid #6c737f;
  border-radius: 8px;
  background-color: var(--white);
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-big);
  font-weight: 500;
  line-height: 28px;
  letter-spacing: 0.03px;
  color: #3b4648;
  outline: none;
  resize: none;
  transition: border-color 0.2s ease;
}

.form-textarea::placeholder {
  color: #6c737f;
  font-weight: 500;
}

.form-textarea:focus {
  border-color: var(--primary-blue);
}

.form-textarea:disabled {
  background-color: #f3f4f6;
  cursor: not-allowed;
  color: #9ca3af;
  border-color: #e5e7eb;
}

/* Form Transition */
.form-fade-enter-active,
.form-fade-leave-active {
  transition: all 0.3s ease;
}

.form-fade-enter-from {
  opacity: 0;
  transform: translateY(-10px);
}

.form-fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

@media (max-width: 1200px) {
  .price-summary {
    width: 100%;
  }
}
:deep(.tt-trigger) {
  width: 100%;
}

.tooltip-wrapper {
  width: 100%;
}

/* Mobile Collapsed View */
.price-summary-mobile-collapsed {
  background-color: var(--white);
  border-radius: 12px 12px 0 0;
  padding: 12px;
  width: 100%;
  box-shadow:
    0px 1px 2px 0px rgba(0, 0, 0, 0.3),
    0px 2px 6px 2px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  gap: 16px;
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  cursor: grab;
  user-select: none;
  touch-action: pan-y;
}

.price-summary-mobile-collapsed:active {
  cursor: grabbing;
}

.mobile-collapsed-handle {
  width: 40px;
  height: 4px;
  background-color: #d8dae5;
  border-radius: 2px;
  margin: 0 auto 8px;
  flex-shrink: 0;
}

.price-summary-mobile-collapsed .price-breakdown {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.price-summary-mobile-collapsed .price-label {
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-big);
  font-weight: 400;
  line-height: 24px;
  color: #3b4648;
}

.price-summary-mobile-collapsed .price-label-currency {
  font-size: var(--fs-big);
}

.price-summary-mobile-collapsed .price-value {
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-big);
  font-weight: 400;
  line-height: 24px;
  color: #3b4648;
}

.price-summary-mobile-collapsed .price-value.bold {
  font-weight: 700;
}

.price-summary-mobile-collapsed .price-divider {
  width: 100%;
  height: 1px;
  background-color: #e5e7eb;
}

@media (max-width: 768px) {
  .price-summary-mobile-collapsed {
    border-radius: 12px 12px 0 0;
  }
}
</style>
